import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { products } from '../data/products';
import { ShoppingCart, Filter, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
interface ProductArchivePageProps {
  onNavigate: (page: 'home' | 'archive' | 'detail', productId?: number) => void;
}
export function ProductArchivePage({
  onNavigate
}: ProductArchivePageProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedVehicle, setSelectedVehicle] = useState<string>('All');
  const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];
  const vehicles = ['All', ...Array.from(new Set(products.map(p => p.vehicle)))];
  const filteredProducts = products.filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory;
    const vehicleMatch = selectedVehicle === 'All' || product.vehicle === selectedVehicle;
    return categoryMatch && vehicleMatch;
  });
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-216">
      <Header data-id="element-217" />

      <main className="flex-grow" data-id="element-218">
        {/* Page Header */}
        <div className="bg-charcoal text-white py-16 relative overflow-hidden" data-id="element-219">
          <div className="absolute inset-0 bg-hero-pattern opacity-20 bg-cover bg-center" data-id="element-220" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-221" />

          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 relative z-10" data-id="element-222">
            <div className="flex flex-col gap-2" data-id="element-223">
              <div className="flex items-center gap-2 text-gray-400 text-sm font-heading uppercase italic tracking-wider" data-id="element-224">
                <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-225">
                  Home
                </button>
                <span data-id="element-226">/</span>
                <span className="text-primary" data-id="element-227">Shop</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase italic tracking-tighter" data-id="element-228">
                Performance <span className="text-primary" data-id="element-229">Parts</span>
              </h1>
              <p className="text-gray-400 max-w-2xl text-lg mt-2 font-sans" data-id="element-230">
                Browse our complete catalog of precision-engineered suspension
                components.
              </p>
            </div>
          </div>
        </div>

        {/* Filters & Content */}
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-id="element-231">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-232">
            {/* Sidebar Filters */}
            <div className="w-full lg:w-64 flex-shrink-0 space-y-8" data-id="element-233">
              <div className="bg-white p-6 border border-gray-200 shadow-sm rounded-sm" data-id="element-234">
                <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-100" data-id="element-235">
                  <Filter className="w-5 h-5 text-primary" data-id="element-236" />
                  <h3 className="font-heading font-bold text-xl uppercase italic text-charcoal" data-id="element-237">
                    Filters
                  </h3>
                </div>

                <div className="space-y-6" data-id="element-238">
                  <div data-id="element-239">
                    <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3" data-id="element-240">
                      Category
                    </h4>
                    <div className="space-y-2" data-id="element-241">
                      {categories.map(category => <label key={category} className="flex items-center gap-3 cursor-pointer group" data-id="element-242">
                          <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${selectedCategory === category ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`} data-id="element-243">
                            {selectedCategory === category && <div className="w-2 h-2 bg-white" data-id="element-244" />}
                          </div>
                          <input type="radio" name="category" className="hidden" checked={selectedCategory === category} onChange={() => setSelectedCategory(category)} data-id="element-245" />
                          <span className={`text-sm font-medium transition-colors ${selectedCategory === category ? 'text-primary' : 'text-gray-600 group-hover:text-charcoal'}`} data-id="element-246">
                            {category}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-247">
                    <h4 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3" data-id="element-248">
                      Vehicle
                    </h4>
                    <div className="space-y-2" data-id="element-249">
                      {vehicles.map(vehicle => <label key={vehicle} className="flex items-center gap-3 cursor-pointer group" data-id="element-250">
                          <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${selectedVehicle === vehicle ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`} data-id="element-251">
                            {selectedVehicle === vehicle && <div className="w-2 h-2 bg-white" data-id="element-252" />}
                          </div>
                          <input type="radio" name="vehicle" className="hidden" checked={selectedVehicle === vehicle} onChange={() => setSelectedVehicle(vehicle)} data-id="element-253" />
                          <span className={`text-sm font-medium transition-colors ${selectedVehicle === vehicle ? 'text-primary' : 'text-gray-600 group-hover:text-charcoal'}`} data-id="element-254">
                            {vehicle}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            <div className="flex-grow" data-id="element-255">
              <div className="flex justify-between items-center mb-6" data-id="element-256">
                <p className="text-gray-500 font-medium" data-id="element-257">
                  {filteredProducts.length} Products Found
                </p>
                <div className="flex items-center gap-2" data-id="element-258">
                  <span className="text-sm text-gray-500 uppercase font-bold" data-id="element-259">
                    Sort By:
                  </span>
                  <button className="flex items-center gap-1 text-sm font-bold text-charcoal hover:text-primary transition-colors" data-id="element-260">
                    Featured <ChevronDown className="w-4 h-4" data-id="element-261" />
                  </button>
                </div>
              </div>

              {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-262">
                  {filteredProducts.map(product => <motion.div layout initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} key={product.id} className="group bg-white border border-gray-100 hover:border-primary/50 transition-all duration-300 flex flex-col h-full" data-id="element-263">
                      <div className="relative h-[250px] bg-gray-50 overflow-hidden p-4 cursor-pointer" onClick={() => onNavigate('detail', product.id)} data-id="element-264">
                        <img src={product.image} alt={product.name} className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500 mix-blend-multiply" data-id="element-265" />
                        {!product.inStock && <div className="absolute top-0 right-0 bg-gray-500 text-white text-xs font-bold px-2 py-1 italic uppercase skew-x-[-10deg]" data-id="element-266">
                            Out of Stock
                          </div>}
                        {product.inStock && <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-2 py-1 italic uppercase skew-x-[-10deg]" data-id="element-267">
                            In Stock
                          </div>}
                        <button className="absolute bottom-0 left-0 w-full bg-charcoal text-white py-3 font-heading font-bold uppercase italic tracking-wider translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex items-center justify-center gap-2" data-id="element-268">
                          <ShoppingCart className="w-4 h-4" data-id="element-269" /> View Details
                        </button>
                      </div>
                      <div className="p-6 flex flex-col flex-grow" data-id="element-270">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-271">
                          {product.category}
                        </div>
                        <h3 className="text-xl font-heading font-bold text-charcoal uppercase italic leading-none mb-2 cursor-pointer hover:text-primary transition-colors" onClick={() => onNavigate('detail', product.id)} data-id="element-272">
                          {product.name}
                        </h3>
                        <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-100" data-id="element-273">
                          <p className="text-2xl font-heading font-bold text-primary italic" data-id="element-274">
                            {product.price}
                          </p>
                          <span className="text-xs font-medium text-gray-400" data-id="element-275">
                            {product.vehicle}
                          </span>
                        </div>
                      </div>
                    </motion.div>)}
                </div> : <div className="text-center py-20 bg-gray-50 border border-dashed border-gray-300 rounded-lg" data-id="element-276">
                  <h3 className="text-xl font-heading font-bold text-gray-400 uppercase italic" data-id="element-277">
                    No products found
                  </h3>
                  <p className="text-gray-500 mt-2" data-id="element-278">
                    Try adjusting your filters to see more results.
                  </p>
                  <button onClick={() => {
                setSelectedCategory('All');
                setSelectedVehicle('All');
              }} className="mt-4 text-primary font-bold hover:underline" data-id="element-279">
                    Clear Filters
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-280" />
    </div>;
}